import { HsaGaleOnboardingEventKind } from "@redotech/crdb-enums/conversion/hsa-gale-onboarding-event-kind";
import { hsaGaleOnboardingEventDetailsSchema } from "@redotech/redo-conversion-hsa-common/hsa-gale-onboarding-event";
import {
  galeKycAddressDefaultsSchema,
  galeKycFieldsSchema,
} from "@redotech/redo-conversion-hsa-common/rpc-schemas/gale-kyc";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * RPC contract for programmatic Gale merchant onboarding (CRO-1616). This is
 * the camelCase, client-facing surface; the server maps it to/from Gale's
 * snake_case wire format (`redo/conversion/hsa/gale-partner`). Kept independent
 * of that server-only package so the enums can be shared with the admin UI
 * without pulling Node-side code into the client bundle. Secrets are included
 * only in registerGaleMerchant and reissueGaleApiKey outputs — shown once.
 */

/** Merchant onboarding lifecycle. Mirrors Gale's `stage`. */
export const galeOnboardingStageSchema = z.enum([
  "pending",
  "review",
  "ready_for_kyc",
  "kyc_in_progress",
  "live",
  "action_needed",
]);
export type GaleOnboardingStage = z.infer<typeof galeOnboardingStageSchema>;

const GALE_MIN_PERCENTAGE_FEE_BPS = 290;

const galeKeyEnvironmentSchema = z.enum(["test", "live"]);

/** Public key metadata returned to the client — never the secret. */
const galeApiKeyMetadataSchema = z.object({
  id: z.string(),
  environment: galeKeyEnvironmentSchema,
  expiresAt: z.string().nullable(),
});

/** Key shape returned only at registration — includes the one-time secret. */
const galeApiKeyWithSecretSchema = z.object({
  id: z.string(),
  environment: galeKeyEnvironmentSchema,
  secret: z.string(),
  expiresAt: z.string().nullable(),
});

const galeOnboardingStatusSchema = z.object({
  stage: galeOnboardingStageSchema,
  canRequestKyc: z.boolean(),
  paymentsEnabled: z.boolean(),
});

// Presence of `lmnFeeCents` selects LMN pricing over standard processing.
const galePricingInputSchema = z.object({
  percentageFeeBps: z.number().int().min(GALE_MIN_PERCENTAGE_FEE_BPS),
  flatFeeCents: z.number().int().nonnegative(),
  lmnFeeCents: z.number().int().nonnegative().optional(),
});

const galePricingOutputSchema = z.object({
  percentageFeeBps: z.number(),
  flatFeeCents: z.number(),
  lmnFeeCents: z.number().nullable(),
});

/** One row of the watcher's ledger. `details` is kind-specific: `issues` on
 * the KYC kinds, `productCount` / `eligibilityPushedAt` on the catalog kinds. */
const galeOnboardingEventSchema = z.object({
  id: z.string(),
  kind: createEnum(HsaGaleOnboardingEventKind),
  previousStage: galeOnboardingStageSchema.nullable(),
  stage: galeOnboardingStageSchema,
  details: hsaGaleOnboardingEventDetailsSchema,
  observedAt: z.string(),
  // When the Slack message was posted; null when it was not (history-only
  // kind, channel unconfigured, or the post failed).
  notifiedAt: z.string().nullable(),
  // When the merchant was emailed about this event; null when the kind has no
  // merchant email or it was not sent. Absent = pre-email server.
  merchantNotifiedAt: z.string().nullable().optional(),
});
export type GaleOnboardingEvent = z.infer<typeof galeOnboardingEventSchema>;

// The merchant's live onboarding state — shared by getGaleOnboardingStatus and
// adoptGaleMerchant. Never includes key secrets.
const galeMerchantStatusSchema = z.object({
  merchantId: z.string(),
  stage: galeOnboardingStageSchema,
  canRequestKyc: z.boolean(),
  paymentsEnabled: z.boolean(),
  pricing: galePricingOutputSchema.nullable(),
  apiKeys: z.array(galeApiKeyMetadataSchema),
});

// ============ 1. Register a merchant ============

export const registerGaleMerchant = {
  name: "registerGaleMerchant" as const,
  // Brand name and store URL are derived server-side from the team. Email can
  // be overridden by the admin; if omitted, the team's email is used.
  input: z.object({
    teamId: zExt.objectId(),
    email: z.string().email().optional(),
    pricing: galePricingInputSchema.optional(),
  }),
  output: z.object({
    merchantId: z.string(),
    stage: galeOnboardingStageSchema,
    canRequestKyc: z.boolean(),
    paymentsEnabled: z.boolean(),
    apiKeys: z.object({
      test: galeApiKeyWithSecretSchema.optional(),
      live: galeApiKeyWithSecretSchema.optional(),
    }),
  }),
};

/** Provenance of the stored KYC payload — who filled it out and when — without
 * carrying the PII payload itself. `savedAt` is null on pre-provenance rows. */
const galePendingKycSummarySchema = z.object({
  source: z.enum(["admin", "merchant"]),
  savedBy: z.string().nullable(),
  savedAt: z.string().nullable(),
  // False = saved with local validation only (Gale refused on stage); null on
  // rows saved before this was recorded.
  validatedByGale: z.boolean().nullable(),
});

// ============ 2. Prepare a KYC link ============

/** Validates the payload against Gale (a pre-flight mint, URL discarded) and
 * persists it for the merchant's stable dashboard link to mint from later. */
export const prepareGaleKycLink = {
  name: "prepareGaleKycLink" as const,
  input: galeKycFieldsSchema.extend({ teamId: zExt.objectId() }),
  // Null = stage-only refusal, payload persisted with local validation only.
  // Optional (and the top-level mirror of its fields) = deploy-skew tolerance.
  output: z.object({
    validation: galeOnboardingStatusSchema.nullable().optional(),
    stage: galeOnboardingStageSchema.optional(),
    canRequestKyc: z.boolean().optional(),
    paymentsEnabled: z.boolean().optional(),
  }),
};

// ============ 3. Get a merchant / onboarding status ============

export const getGaleOnboardingStatus = {
  name: "getGaleOnboardingStatus" as const,
  input: z.object({ teamId: zExt.objectId() }),
  // `merchant` is null when the team has not been registered with Gale yet, so
  // the UI can render the registration prompt without an error round-trip.
  // `teamDefaults` is the identity Gale will be registered with (from the team
  // record) — shown read-only in the register modal.
  output: z.object({
    teamDefaults: z.object({
      brandName: z.string(),
      email: z.string(),
      storeUrl: z.string(),
      address: galeKycAddressDefaultsSchema.nullable(),
      ein: z.string().nullable(),
    }),
    merchant: galeMerchantStatusSchema.nullable(),
    // Null/absent (absent = pre-`pendingKyc` server) until a payload is saved;
    // presence switches the card's KYC button from "fill out" to "update".
    pendingKyc: galePendingKycSummarySchema.nullable().optional(),
    // The stored merchant id when Gale no longer returns it (MERCHANT_NOT_FOUND,
    // e.g. Gale merged it into another merchant). `merchant` is then null so the
    // admin can link the right merchant or register again. Absent = older server.
    unresolvedMerchantId: z.string().nullable().optional(),
    // The watcher's observed changes, newest first; empty for an unregistered
    // team or when the ledger read fails. Absent = pre-`events` server.
    events: z.array(galeOnboardingEventSchema).optional(),
  }),
};

// ============ 3b. Read the stored KYC payload ============

/** The stored KYC payload for prefilling the update modal. PII — fetched only
 * when the admin opens the modal, never on card load. */
export const getGalePendingKycDetails = {
  name: "getGalePendingKycDetails" as const,
  input: z.object({ teamId: zExt.objectId() }),
  output: z.object({ request: galeKycFieldsSchema.nullable() }),
};

// ============ 4. Update pricing ============

export const updateGalePricing = {
  name: "updateGalePricing" as const,
  input: z.object({ teamId: zExt.objectId(), pricing: galePricingInputSchema }),
  output: z.object({ pricing: galePricingOutputSchema }),
};

// ============ 5. Re-issue an API key ============

export const reissueGaleApiKey = {
  name: "reissueGaleApiKey" as const,
  input: z.object({
    teamId: zExt.objectId(),
    environment: galeKeyEnvironmentSchema,
  }),
  // Unlike every other output, this includes the key `secret` — recovering a
  // usable secret is the entire purpose. Shown once for the admin to hand off.
  output: z.object({
    apiKey: z.object({
      id: z.string(),
      environment: galeKeyEnvironmentSchema,
      secret: z.string(),
      expiresAt: z.string().nullable(),
    }),
  }),
};

// ============ 6. Adopt an existing Gale merchant ============

export const adoptGaleMerchant = {
  name: "adoptGaleMerchant" as const,
  // Links a Gale merchant that already exists (looked up by email) to this team
  // — for merchants onboarded before this flow. `merchant` is null when none
  // matches. Key secrets are not recoverable here; use reissueGaleApiKey.
  input: z.object({ teamId: zExt.objectId(), email: z.string().min(1) }),
  output: z.object({ merchant: galeMerchantStatusSchema.nullable() }),
};

// ============ 7. Upload product catalog ============

export const uploadGaleCatalog = {
  name: "uploadGaleCatalog" as const,
  input: z.object({ teamId: zExt.objectId() }),
  output: z.object({
    rowCount: z.number(),
    filename: z.string(),
    sizeBytes: z.number(),
    receivedAt: z.string(),
  }),
};

export { galeApiKeyMetadataSchema, galeOnboardingStatusSchema };
